/* eslint-disable @typescript-eslint/no-misused-promises */
import React from 'react'
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  styled,
  useTheme
} from '@mui/material'
import { useFantaHockeyContext } from 'providers/AuthProvider'
import { useDocumentTitle } from '@uidotdev/usehooks'
import { useNavigate } from 'react-router'
import { initializeMsalInstance } from 'features/login/setup/microsoft/microsoftPublicClient'
import { type IPublicClientApplication } from '@azure/msal-browser'
import { OAuthProvider, type OAuthLoginResponse } from 'features/login/models/generic/OAuthResponse'
import { type CredentialResponse, GoogleLogin } from '@react-oauth/google'
import { useSnackbar } from 'hooks/notification/UseSnackbar'
import { Lock } from '@mui/icons-material'
import { useMutation } from '@tanstack/react-query'
import { ApiMutationKeys, ApiMutationService } from 'services/mutation/apiMutationService'
import useFantaHockeyStore from 'hooks/store/FantaHockeyStateStore'
import { isMobile } from 'react-device-detect'

const AppLoginLoading: React.FC = React.memo((): JSX.Element => {
  return <>
    <Box my={4}>
      <CircularProgress color='info' />
    </Box>
  </>
})
AppLoginLoading.displayName = 'AppLoginLoading'

const AppLogin: React.FC = React.memo((): JSX.Element => {
  const theme = useTheme()
  useDocumentTitle('FantaHockey | Login')
  const navigate = useNavigate()
  const fantaHockeyAuth = useFantaHockeyContext()
  const [msalInstance, setMsalInstance] = React.useState<IPublicClientApplication>()
  const [isLoading, setLoading] = React.useState(false)
  const { openSnackbar } = useSnackbar()
  const store = useFantaHockeyStore()

  const { mutateAsync: loginWithMicrosoft } = useMutation<OAuthLoginResponse, unknown, string>({
    mutationKey: [ApiMutationKeys.LoginWithMicrosoftMutation],
    mutationFn: async (idToken: string) => {
      return (await ApiMutationService[ApiMutationKeys.LoginWithMicrosoftMutation](idToken)).data
    }
  })

  const { mutateAsync: loginWithGoogle } = useMutation<OAuthLoginResponse, unknown, string>({
    mutationKey: [ApiMutationKeys.LoginWithGoogleMutation],
    mutationFn: async (idToken: string) => {
      return (await ApiMutationService[ApiMutationKeys.LoginWithGoogleMutation](idToken)).data
    }
  })
  React.useLayoutEffect(() => {
    if (fantaHockeyAuth.isAuthenticated) {
      navigate('/home', { replace: true })
    }
  }, [fantaHockeyAuth.isAuthenticated])
  React.useEffect(() => {
    const setupMsalLogin = async (): Promise<void> => {
      const instance = await initializeMsalInstance()
      setMsalInstance(instance)
    }
    void setupMsalLogin()
  }, [])

  // Store MSAL instance when available
  React.useEffect(() => {
    if (msalInstance !== undefined) {
      fantaHockeyAuth.storeMsalInstance(msalInstance)
    }
  }, [msalInstance])

  const loginMicrosoftCallbackHandler = async (idToken: string): Promise<OAuthLoginResponse> => {
    return await loginWithMicrosoft(idToken)
  }

  const loginGoogleCallbackHandler = async (idToken: string | undefined): Promise<OAuthLoginResponse> => {
    return await loginWithGoogle(idToken ?? '')
  }

  const loginMicrosoftAuthentication = async (): Promise<void> => {
    try {
      if (isLoading) return
      setLoading(true)
      const response = await msalInstance?.loginPopup()
      if (response?.account !== undefined) {
        const { idToken } = response
        const oAuthLoginResponse = await loginMicrosoftCallbackHandler(idToken)
        console.log(oAuthLoginResponse)
        if (oAuthLoginResponse.isAuthenticated) {
          setLoading(false)
          const loginModel = {
            securityToken: idToken ?? '',
            name: oAuthLoginResponse.name,
            isAuthenticated: true,
            email: oAuthLoginResponse.email,
            provider: OAuthProvider.Microsoft,
            isFirstLogin: oAuthLoginResponse.isFirstLogin
          }
          fantaHockeyAuth.login(loginModel)
          await store.getInitialData()
          openSnackbar('Logged In', 'success')
        }
      } else {
        setLoading(false)
      }
    } catch (error: unknown) {
      console.error(error)
      setLoading(false)
    }
  }

  const googleLoginCallbackHandler = async (credentialResponse: CredentialResponse): Promise<void> => {
    try {
      if (isLoading) return
      setLoading(true)
      const oAuthLoginResponse = await loginGoogleCallbackHandler(credentialResponse.credential)
      if (oAuthLoginResponse.isAuthenticated) {
        setLoading(false)
        const loginModel = {
          securityToken: credentialResponse.credential ?? '',
          name: oAuthLoginResponse.name,
          isAuthenticated: true,
          email: oAuthLoginResponse.email,
          provider: OAuthProvider.Google,
          isFirstLogin: oAuthLoginResponse.isFirstLogin
        }
        fantaHockeyAuth.login(loginModel)
        await store.getInitialData()
        openSnackbar('Logged In', 'success')
      } else {
        setLoading(false)
      }
    } catch (error: unknown) {
      setLoading(false)
    }
  }

  const CenterBox = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
    textAlign: 'center'
  })

  const Subtitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary
  }))

  if (fantaHockeyAuth.isAuthenticated) {
    return <></>
  }

  const LoginCard: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
    return (
      <Box textAlign="center" m={{ xs: 2, sm: 3, md: 4 }} sx={{ width: '100%', maxWidth: 380 }}>
        <Card
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            backdropFilter: 'blur(6px)',
            boxShadow: theme.shadows[8],
            transition: 'box-shadow 200ms ease, transform 200ms ease',
            '&:hover': {
              boxShadow: theme.shadows[12]
            }
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>{children}</CardContent>
        </Card>
      </Box>
    )
  }

  const MicrosoftLoginButton = (): JSX.Element => (
    <Button
      fullWidth
      size="large"
      variant="contained"
      color="primary"
      disabled={isLoading}
      onClick={async () => { await loginMicrosoftAuthentication() }}
      startIcon={
        <Box component="img" src={'./assets/images/microsoft-logo.png'} alt={'Microsoft'} sx={{ width: 20, height: 20 }} />
      }
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 2,
        py: 1.25,
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' },
        transition: 'transform 150ms ease'
      }}
    >
      <Typography>{'Continue with Microsoft'}</Typography>
    </Button>
  )

  const GoogleLoginButton = (): JSX.Element => (
    <Box position="relative" width="100%">
      <Button
        fullWidth
        size="large"
        variant="outlined"
        color="inherit"
        disabled={isLoading}
        startIcon={
          <Box component="img" src={'./assets/images/google-login-logo.svg'} alt={'Google'} sx={{ width: 20, height: 20 }} />
        }
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          py: 1.25,
          borderColor: alpha(theme.palette.text.primary, 0.2),
          '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.3), transform: 'translateY(-1px)' },
          transition: 'transform 150ms ease'
        }}
      >
       <Typography>{'Continue with Google'}</Typography>
      </Button>
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, opacity: 0.00001, pointerEvents: isLoading ? 'none' : 'auto' }}>
      {/* Invisible overlay to capture click for Google auth, this is a workaround */}
        <GoogleLogin
          shape='rectangular'
          size='large'
          useOneTap={true}
          width={'150%'}
          onSuccess={async (credentialResponse: CredentialResponse) => {
            await googleLoginCallbackHandler(credentialResponse)
          }}
          onError={() => {
            openSnackbar('Login Failed', 'error')
          }}
        />
      </Box>
    </Box>
  )

  return (
    <>
  <Box
        sx={{
          minHeight: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 64px)',
          overflowY: 'hidden',
          display: 'grid',
          placeItems: 'center',
          // Soft, theme-aware background
          background: (theme) => `radial-gradient(1000px 600px at 10% 0%, ${alpha(theme.palette.primary.light, 0.10)} 0, transparent 60%),
               radial-gradient(900px 500px at 100% 100%, ${alpha(theme.palette.secondary.light, 0.08)} 0, transparent 60%)`
        }}
      >
        <LoginCard>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main'
              }}
            >
              <Lock sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Welcome back
            </Typography>
            <Subtitle>
              Sign in to continue to FantaHockey
            </Subtitle>
          </Box>

          <Stack spacing={2.5} mt={4}>
            <MicrosoftLoginButton />
            <GoogleLoginButton />
          </Stack>

          {isLoading && (
            <CenterBox>
              <AppLoginLoading />
            </CenterBox>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary">
            By continuing, you agree to our Terms and acknowledge our Privacy Policy.
          </Typography>
        </LoginCard>
      </Box>
    </>
  )
})

AppLogin.propTypes = {

}

AppLogin.displayName = 'AppLogin'
export default AppLogin
